import React from 'react';
import { Box, TextField, MenuItem } from '@mui/material';
import CollapsibleSection from '../../../shared/components/CollapsibleSection';
import { PromptOwnerType, PromptTypeType, PromptAccountScopeType, SkillApplicabilityType } from '../../../types/backend';
import { CREATABLE_PROMPT_TYPES, PROMPT_TYPE_LABELS, SKILL_APPLICABILITY_LABELS } from '../../../shared/constants/promptTypes';
import { PromptFormData } from '../hooks/usePromptManagement';

interface PromptSettingsProps {
  formData: PromptFormData;
  isNewPrompt: boolean;
  fieldErrors: Record<string, string | null>;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onChange: <K extends keyof PromptFormData>(field: K, value: PromptFormData[K]) => void;
}

const PromptSettings: React.FC<PromptSettingsProps> = ({
  formData,
  isNewPrompt,
  fieldErrors,
  expanded,
  onExpandedChange,
  onChange,
}) => {
  const isPlaybookType = formData.type === 'playbook';
  const isSkillType = formData.type === 'skill';

  return (
    <Box sx={{ px: 3, py: 1.5 }}>
      <CollapsibleSection
        title="Settings"
        expanded={expanded}
        onChange={onExpandedChange}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            error={!!fieldErrors.name}
            helperText={fieldErrors.name || (isNewPrompt ? "Auto-generated from title" : undefined)}
            placeholder={isSkillType ? "athena-cost-data-querying" : "aws_cost_optimization_review"}
            size="small"
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly: !isNewPrompt }}
            fullWidth
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => onChange('description', e.target.value)}
            error={!!fieldErrors.description}
            helperText={fieldErrors.description || (isSkillType ? `${formData.description.length} / 100 min characters` : undefined)}
            placeholder="Brief description"
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label="Type"
            value={formData.type}
            onChange={(e) => onChange('type', e.target.value as PromptTypeType)}
            disabled={!isNewPrompt}
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          >
            {CREATABLE_PROMPT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{PROMPT_TYPE_LABELS[t]}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Owner"
            value={formData.owner}
            onChange={(e) => onChange('owner', e.target.value as PromptOwnerType)}
            disabled={!isNewPrompt}
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          >
            <MenuItem value="customer">This Customer</MenuItem>
            <MenuItem value="global">Cloud2 Global</MenuItem>
          </TextField>
          {isPlaybookType && (
            <TextField
              select
              label="Account Scope"
              value={formData.accountScope}
              onChange={(e) => onChange('accountScope', e.target.value as PromptAccountScopeType)}
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ gridColumn: '1 / -1' }}
            >
              <MenuItem value="none">None (Knowledge-only)</MenuItem>
              <MenuItem value="operations">Operations Account</MenuItem>
              <MenuItem value="customer">Customer Account(s)</MenuItem>
              <MenuItem value="both">Both (Operations + Customer)</MenuItem>
            </TextField>
          )}
          {isSkillType && (
            <TextField
              select
              label="Available to"
              value={formData.agentType}
              onChange={(e) => onChange('agentType', e.target.value as SkillApplicabilityType)}
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ gridColumn: '1 / -1' }}
            >
              {(Object.keys(SKILL_APPLICABILITY_LABELS) as SkillApplicabilityType[]).map((a) => (
                <MenuItem key={a} value={a}>{SKILL_APPLICABILITY_LABELS[a]}</MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      </CollapsibleSection>
    </Box>
  );
};

export default PromptSettings;
